import React, { useEffect, useState } from 'react'
import {
    Button,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    TextField,
    useTheme,
} from '@material-ui/core'
import { Rating } from '@material-ui/lab'
import { toast } from 'react-toastify'
import { axiosInstance } from '../../helpers'
import { Container } from '../../components'

export type RateVendorTarget = {
    id: number
    name: string
    professionalism?: number | null
    work_quality?: number | null
}

type DraftRating = {
    professionalism: number
    work_quality: number
    review: string
}

interface Props {
    open: boolean
    onClose: () => void
    vendors: RateVendorTarget[]
    title?: string
    onSaved?: () => void
    scheduleId?: number
}

const SUGGESTED_REVIEW_PHRASES = [
    'Always showed up on time',
    'Communicated well with the office',
    'Quality work, few go-backs',
    'Finished units on schedule',
    'Easy to work with',
    'Responsive to requests',
    'Cleaned up after themselves',
    'Fair pricing',
    'Would hire again',
    'Needed extra follow-up',
    'Slow to get started',
    'Communication could be better',
]

const EMPTY_DRAFT: DraftRating = {
    professionalism: 0,
    work_quality: 0,
    review: '',
}

export const RateVendorDialog = (props: Props) => {
    const { open, onClose, vendors, title, onSaved, scheduleId } = props
    const theme = useTheme()
    const [drafts, setDrafts] = useState<Record<number, DraftRating>>({})
    const [saving, setSaving] = useState(false)

    useEffect(() => {
        if (!open) {
            return
        }
        const next: Record<number, DraftRating> = {}
        vendors.forEach((vendor) => {
            next[vendor.id] = {
                professionalism: Number(vendor.professionalism) || 0,
                work_quality: Number(vendor.work_quality) || 0,
                review: '',
            }
        })
        setDrafts(next)
    }, [open, vendors])

    const updateStars = (
        vendorId: number,
        field: 'professionalism' | 'work_quality',
        value: number | null,
    ) => {
        setDrafts((prev) => ({
            ...prev,
            [vendorId]: {
                ...(prev[vendorId] || EMPTY_DRAFT),
                [field]: value ?? 0,
            },
        }))
    }

    const updateReview = (vendorId: number, review: string) => {
        setDrafts((prev) => ({
            ...prev,
            [vendorId]: {
                ...(prev[vendorId] || EMPTY_DRAFT),
                review,
            },
        }))
    }

    const appendSuggestion = (vendorId: number, phrase: string) => {
        setDrafts((prev) => {
            const current = prev[vendorId] || EMPTY_DRAFT
            if (current.review.includes(phrase)) {
                return prev
            }
            const sentence = phrase.endsWith('.') ? phrase : `${phrase}.`
            const existing = current.review.trim()
            const prefix =
                existing && !/[.!?]$/.test(existing) ? `${existing}.` : existing
            const review = prefix ? `${prefix} ${sentence}` : sentence
            return {
                ...prev,
                [vendorId]: {
                    ...current,
                    review,
                },
            }
        })
    }

    const handleSave = async () => {
        const toSave = vendors.filter((vendor) => {
            const draft = drafts[vendor.id]
            return draft && draft.professionalism > 0 && draft.work_quality > 0
        })
        if (toSave.length === 0) {
            toast.error(
                'Set both professionalism and work quality (at least 1★) before saving.',
            )
            return
        }

        setSaving(true)
        try {
            await Promise.all(
                toSave.map((vendor) => {
                    const draft = drafts[vendor.id]
                    const payload: {
                        vendor_id: number
                        professionalism: number
                        work_quality: number
                        review?: string
                        schedule_id?: number
                    } = {
                        vendor_id: vendor.id,
                        professionalism: draft.professionalism,
                        work_quality: draft.work_quality,
                    }
                    const review = draft.review.trim()
                    if (review) {
                        payload.review = review
                    }
                    if (scheduleId != null) {
                        payload.schedule_id = scheduleId
                    }
                    return axiosInstance.post(
                        'company/apartment_vendors/set_rating/',
                        payload,
                    )
                }),
            )
            toast.success(
                toSave.length === 1
                    ? 'Vendor rating saved'
                    : `Saved ratings for ${toSave.length} vendors`,
            )
            onSaved?.()
            onClose()
        } catch {
            toast.error('Unable to save vendor rating(s). Please try again.')
        } finally {
            setSaving(false)
        }
    }

    return (
        <Dialog
            open={open}
            onClose={saving ? undefined : onClose}
            maxWidth="md"
            fullWidth
        >
            <DialogTitle>{title || 'Rate vendors'}</DialogTitle>
            <DialogContent>
                <Container direction="column" style={{ gap: theme.spacing(2) }}>
                    {vendors.length === 0 ? (
                        <span>No vendors to rate.</span>
                    ) : (
                        vendors.map((vendor) => {
                            const draft = drafts[vendor.id] || EMPTY_DRAFT
                            return (
                                <Container
                                    key={vendor.id}
                                    direction="column"
                                    style={{
                                        padding: theme.spacing(1.5),
                                        border: `1px solid ${theme.palette.grey[300]}`,
                                        borderRadius: 8,
                                        gap: theme.spacing(0.5),
                                    }}
                                >
                                    <span
                                        style={{
                                            fontWeight: 700,
                                            fontSize: 18,
                                            lineHeight: '24px',
                                        }}
                                    >
                                        {vendor.name}
                                    </span>
                                    <span>Professionalism</span>
                                    <Container
                                        alignItems="center"
                                        style={{ gap: theme.spacing(1) }}
                                    >
                                        <Rating
                                            name={`rate-vendor-${vendor.id}-prof`}
                                            precision={0.5}
                                            size="large"
                                            value={draft.professionalism}
                                            onChange={(_, value) =>
                                                updateStars(
                                                    vendor.id,
                                                    'professionalism',
                                                    value,
                                                )
                                            }
                                        />
                                        <span
                                            style={{
                                                minWidth: 28,
                                                color: theme.palette.grey[700],
                                                fontWeight: 600,
                                            }}
                                        >
                                            {draft.professionalism > 0
                                                ? draft.professionalism.toFixed(
                                                      1,
                                                  )
                                                : '—'}
                                        </span>
                                    </Container>
                                    <span>Quality of work</span>
                                    <Container
                                        alignItems="center"
                                        style={{ gap: theme.spacing(1) }}
                                    >
                                        <Rating
                                            name={`rate-vendor-${vendor.id}-quality`}
                                            precision={0.5}
                                            size="large"
                                            value={draft.work_quality}
                                            onChange={(_, value) =>
                                                updateStars(
                                                    vendor.id,
                                                    'work_quality',
                                                    value,
                                                )
                                            }
                                        />
                                        <span
                                            style={{
                                                minWidth: 28,
                                                color: theme.palette.grey[700],
                                                fontWeight: 600,
                                            }}
                                        >
                                            {draft.work_quality > 0
                                                ? draft.work_quality.toFixed(1)
                                                : '—'}
                                        </span>
                                    </Container>
                                    <span
                                        style={{ marginTop: theme.spacing(1) }}
                                    >
                                        Review (optional)
                                    </span>
                                    <Container
                                        style={{
                                            flexWrap: 'wrap',
                                            gap: theme.spacing(0.5),
                                        }}
                                    >
                                        {SUGGESTED_REVIEW_PHRASES.map(
                                            (phrase) => (
                                                <Chip
                                                    key={phrase}
                                                    label={phrase}
                                                    size="small"
                                                    clickable
                                                    color={
                                                        draft.review.includes(
                                                            phrase,
                                                        )
                                                            ? 'primary'
                                                            : 'default'
                                                    }
                                                    variant={
                                                        draft.review.includes(
                                                            phrase,
                                                        )
                                                            ? 'default'
                                                            : 'outlined'
                                                    }
                                                    onClick={() =>
                                                        appendSuggestion(
                                                            vendor.id,
                                                            phrase,
                                                        )
                                                    }
                                                />
                                            ),
                                        )}
                                    </Container>
                                    <TextField
                                        multiline
                                        rows={3}
                                        variant="outlined"
                                        size="small"
                                        placeholder="Share how this vendor did on your turns…"
                                        value={draft.review}
                                        onChange={(event) =>
                                            updateReview(
                                                vendor.id,
                                                event.target.value,
                                            )
                                        }
                                    />
                                </Container>
                            )
                        })
                    )}
                </Container>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={saving}>
                    Cancel
                </Button>
                <Button
                    color="primary"
                    variant="contained"
                    onClick={handleSave}
                    disabled={saving || vendors.length === 0}
                >
                    {saving ? <CircularProgress size={20} /> : 'Save'}
                </Button>
            </DialogActions>
        </Dialog>
    )
}

/** Show Rate vendors from 4 days before schedule end_date onward (no end). */
export const isWithinRateVendorsWindow = (
    endDateIso?: string | null,
): boolean => {
    if (!endDateIso) {
        return false
    }
    const end = new Date(endDateIso)
    if (Number.isNaN(end.getTime())) {
        return false
    }
    const windowStart = new Date(end)
    windowStart.setDate(windowStart.getDate() - 4)
    return Date.now() >= windowStart.getTime()
}

export const FRESH_APARTMENT_VENDOR_RATING_DAYS = 30

export const isFreshApartmentVendorRating = (apt?: {
    professionalism?: number | null
    work_quality?: number | null
    rating_updated_at?: string | null
}): boolean => {
    if (!apt) {
        return false
    }
    if (!(Number(apt.professionalism) > 0) || !(Number(apt.work_quality) > 0)) {
        return false
    }
    if (!apt.rating_updated_at) {
        return false
    }
    const updated = new Date(apt.rating_updated_at).getTime()
    if (Number.isNaN(updated)) {
        return false
    }
    return (
        Date.now() - updated <=
        FRESH_APARTMENT_VENDOR_RATING_DAYS * 24 * 60 * 60 * 1000
    )
}
